// @noflow
import type { TextProps } from './Text';
import type { Theme } from '../themes/types';
import React from 'react';
import type { Node as React$Node } from 'react';
import PropTypes from 'prop-types';
import Text, { computeTextStyle } from './Text';
import color from 'color';
import isReactNative from '../../common/app/isReactNative';
import { withTheme } from 'react-fela';

// Universal text input component. By default, it looks like editable text.
// For underline or the other effects, make a new component. Check Field.
// TODO: multiline and rows, use PlatformTextarea, react-autosize-textarea?

export type TextInputProps = TextProps & {
  disabled?: boolean,
  placeholderTextColor?: string,
  theme: Theme,
};

type TextInputContext = {
  TextInput: () => React$Node,
};

const computePlaceholderColor = textColor =>
  color(textColor)
    .fade(0.5)
    .toString();

const TextInput = (props: TextInputProps, { TextInput: PlatformTextInput }: TextInputContext) => {
  const { theme } = props;
  const [textStyle] = computeTextStyle(theme, props);

  const {
    disabled = false,
    height = textStyle.lineHeight / theme.typography.lineHeight,
    // Some user agents (Chrome, RN Android) have default input padding.
    // We need to reset it to ensure consistent rendering across platforms.
    padding = 0,
    placeholderTextColor = computePlaceholderColor(textStyle.color),
    style,
    ...restProps
  } = props;

  const platformProps = isReactNative
    ? {
        editable: !disabled,
        underlineColorAndroid: 'transparent',
        placeholderTextColor,
      }
    : {
        disabled,
      };
  const platformStyle = isReactNative
    ? {}
    : {
        outline: 'none',
        '::placeholder': {
          color: placeholderTextColor,
        },
      };
  return (
    <Text
      as={PlatformTextInput}
      // React Native TextInput needs explicit height.
      // Browsers need explicit height for correct vertical align.
      height={height}
      padding={padding}
      {...(disabled ? { opacity: theme.states.disabled.opacity } : null)}
      {...platformProps}
      {...restProps}
      style={(theme, textStyle) => ({
        ...platformStyle,
        ...(style && style(theme, textStyle)),
      })}
    />
  );
};

TextInput.contextTypes = {
  TextInput: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
};

export default withTheme(TextInput, 'theme');
